import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import PropTypes from "prop-types"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { get, post } from "../../api/axios"
import endpoints from "../../api/endpoints"
import {
  hasAuthSession,
  isAuthGuestPath,
} from "../../helpers/routeAccess"
import { getUserAvatarUrl } from "../../helpers/userAvatar"
import { getUserFullName } from "../../helpers/userDisplay"
import DirectChatDock from "./DirectChatDock"

const ChatDockContext = createContext(null)

/** Notification / deep-link query: `/?dm={other_user_id}&context_id={optional}` */
export const DM_QUERY_KEY = "dm"
export const DM_CONTEXT_QUERY_KEY = "context_id"

const isChatDockRoute = pathname => {
  const path = String(pathname || "")
  if (!path || path === "/logout") return false
  if (isAuthGuestPath(path)) return false
  if (path.startsWith("/onboarding")) return false
  return true
}

const getContactInitials = name => {
  const cleaned = String(name || "").trim()
  if (!cleaned) return "?"
  return cleaned
    .split(/\s+/)
    .map(part => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase()
}

export const ChatDockProvider = ({ children }) => {
  const [openChats, setOpenChats] = useState([])
  const authToken = useSelector(state => state.auth.jid)
  const currentUser = useSelector(state => state.auth.currentUser)
  const location = useLocation()
  const navigate = useNavigate()
  const isAuthenticated = hasAuthSession(authToken)
  const canShowDock = isAuthenticated && isChatDockRoute(location.pathname)
  const handledDmKeyRef = useRef("")

  const openDirectChat = useCallback(contact => {
    if (!contact?.id || contact.isGroup) return
    setOpenChats([{ ...contact, minimized: false }])
  }, [])

  const closeChat = useCallback(id => {
    setOpenChats(current =>
      current.filter(chat => String(chat.id) !== String(id)),
    )
  }, [])

  const setMinimized = useCallback((id, minimized) => {
    setOpenChats(current =>
      current.map(chat =>
        String(chat.id) === String(id) ? { ...chat, minimized } : chat,
      ),
    )
  }, [])

  const minimizeChat = useCallback(id => setMinimized(id, true), [setMinimized])
  const expandChat = useCallback(id => setMinimized(id, false), [setMinimized])

  const stripDmQueryParams = useCallback(() => {
    const params = new URLSearchParams(location.search)
    if (!params.has(DM_QUERY_KEY) && !params.has(DM_CONTEXT_QUERY_KEY)) {
      return
    }
    params.delete(DM_QUERY_KEY)
    params.delete(DM_CONTEXT_QUERY_KEY)
    const nextSearch = params.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
        hash: location.hash,
      },
      { replace: true },
    )
  }, [location.hash, location.pathname, location.search, navigate])

  useEffect(() => {
    if (!isAuthenticated) setOpenChats([])
  }, [isAuthenticated])

  useEffect(() => {
    if (!canShowDock) setOpenChats([])
  }, [canShowDock])

  // Deep link: /?dm={other_user_id}&context_id={optional} → floating DM popup
  useEffect(() => {
    if (!canShowDock) return undefined

    const params = new URLSearchParams(location.search)
    const userId = String(params.get(DM_QUERY_KEY) || "").trim()
    if (!userId) {
      handledDmKeyRef.current = ""
      return undefined
    }

    const contextIdParam = String(
      params.get(DM_CONTEXT_QUERY_KEY) || "",
    ).trim()
    const requestKey = `${userId}:${contextIdParam}`
    if (handledDmKeyRef.current === requestKey) return undefined

    if (currentUser?.id && String(currentUser.id) === userId) {
      handledDmKeyRef.current = requestKey
      stripDmQueryParams()
      return undefined
    }

    let cancelled = false
    handledDmKeyRef.current = requestKey

    const openFromQuery = async () => {
      try {
        let chatId = contextIdParam || null
        let person = null

        // Always get-or-create so we have a context id and (often) participant data.
        // getUser alone frequently fails for other members → title became "Direct Message".
        const [chatRes, userRes, membersRes] = await Promise.all([
          post(
            endpoints.getOrCreateDirectChat(userId),
            {},
            { skipErrorToast: true },
          ),
          get(endpoints.getUser(userId), {
            params: { "with[]": ["profile_media"] },
            skipErrorToast: true,
          }),
          get(endpoints.getMyProjectMembers, {
            params: { page: 1, page_size: 100 },
            skipErrorToast: true,
          }),
        ])

        if (cancelled) return

        if (chatRes?.data?.success) {
          const result = chatRes.data.result || {}
          chatId = chatId || result.id || null
          const participants = Array.isArray(result.participants)
            ? result.participants
            : Array.isArray(result.collaborators)
              ? result.collaborators
              : []
          person =
            result.user ||
            result.other_user ||
            participants.find(p => String(p?.id) === String(userId)) ||
            null
        }

        if (userRes?.data?.success && userRes.data.result) {
          person = userRes.data.result
        }

        if (!getUserFullName(person, { fallback: "" }) && membersRes?.data?.success) {
          const result = membersRes.data.result
          const rows = Array.isArray(result?.data)
            ? result.data
            : Array.isArray(result)
              ? result
              : []
          const row = rows.find(
            item =>
              String(item?.user?.id || item?.user_id || "") === String(userId),
          )
          if (row?.user) person = row.user
        }

        if (!chatId) {
          stripDmQueryParams()
          return
        }

        const name =
          getUserFullName(person, { fallback: "" }) ||
          [person?.first_name, person?.last_name].filter(Boolean).join(" ").trim() ||
          person?.email ||
          "Direct Message"

        openDirectChat({
          id: chatId,
          contextId: chatId,
          userId,
          name,
          initials: getContactInitials(name),
          avatar: getUserAvatarUrl(person) || "",
          isGroup: false,
          projectId: null,
          projectName: "",
          participants: [{ id: userId, name }],
        })
      } catch {
        // Keep silent — invalid/expired deep links should not toast.
      } finally {
        if (!cancelled) stripDmQueryParams()
      }
    }

    openFromQuery()
    return () => {
      cancelled = true
    }
  }, [
    canShowDock,
    currentUser?.id,
    location.search,
    openDirectChat,
    stripDmQueryParams,
  ])

  const value = useMemo(
    () => ({
      openChats,
      openDirectChat,
      closeChat,
      minimizeChat,
      expandChat,
      canShowDock,
    }),
    [
      canShowDock,
      closeChat,
      expandChat,
      minimizeChat,
      openChats,
      openDirectChat,
    ],
  )

  return (
    <ChatDockContext.Provider value={value}>
      {children}
      {canShowDock ? <DirectChatDock /> : null}
    </ChatDockContext.Provider>
  )
}

ChatDockProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useChatDock = () => {
  const value = useContext(ChatDockContext)
  if (!value)
    throw new Error("useChatDock must be used inside ChatDockProvider")
  return value
}
